/**
 * @file formConstants
 * 申し込み部分で用いる定数
 */

import {PayTypeText} from '@/js/util/constants'
import {helperSecondPaymentMonth} from '@/js/util/helper'
import {ADD_MONTH_NEXT, startPeriodAddMonth} from '@/js/util/paymentStartPeriod'

import moment from 'moment'

/**
 * ボ2 第2回目支払月に選べる最終月を決めるパラメータ
 *
 * 「2回目の上限 = 申込月 + 1 + この値」となる。件数ではない点に注意
 * (件数は選択された1回目に応じて後段の diff 補正で削られる)。
 *
 * 起点が翌月のときに 14 とするのは、1回目に最終月を選んだ場合でも
 * 2回目の選択肢を残すため。13 のままにすると 1回目に最終月を選んだ時点で
 * 2回目が0件になる(申込日が1日の場合は配列長が負になり例外)。
 *
 * @param {number} initialAddMonth 1回目の起点(1 = 翌月 / 2 = 翌々月)
 */
const twiceMonthRangeLength = (initialAddMonth) => (initialAddMonth === ADD_MONTH_NEXT ? 14 : 13)

/**
 * お支払い方法
 * @param {string} category 申込種別 (credit or installment)
 * @param {array} finances 金融商品一覧
 * @param {string} finance_category_name 金融カテゴリー名
 */
export const Payment = (category, finances, finance_category_name) => {
  const paymentContract = {
    // 自社割賦
    credit: [
      {
        label: PayTypeText.split,
        value: 'split',
        isShow: true
      },
      {
        label: PayTypeText.split_bonus,
        value: 'split_bonus',
        isShow: true
      },
      {
        label: PayTypeText.once,
        value: 'once',
        isShow: true
      },
      {
        label: PayTypeText.seconds,
        value: 'seconds',
        isShow: true
      },
      {
        label: PayTypeText.monthly,
        value: 'monthly',
        isShow: true
      }
    ],
    // 信販
    installment: [
      {
        label: PayTypeText.split,
        value: 'split',
        isShow: true
      },
      {
        label: PayTypeText.split_bonus,
        value: 'split_bonus',
        isShow: true
      },
      {
        label: PayTypeText.once,
        value: 'once',
        isShow: true
      },
      {
        label: PayTypeText.seconds,
        value: 'seconds',
        isShow: true
      }
    ]
  }

  if (!finances.length || !finance_category_name) {
    return paymentContract[category]
  } else {
    finances.map((item) => {
      if (item.category_name === finance_category_name) {
        const use_pays = ['use_split_pay', 'use_once_pay', 'use_seconds_pay', 'use_split_bonus_pay', 'use_monthly_pay']
        use_pays.map((use) => {
          if (!item[use]) {
            const obj = paymentContract[category].find((contract) => {
              // console.log(contract)
              return contract.value === use.replace('use_', '').replace('_pay', '')
            })
            // console.log(obj, use)
            if (!obj) return false
            obj.isShow = false
          }
        })
      }
    })
    return paymentContract[category]
  }
}

/**
 * 分割回数
 */
export const SplitNumber = () => {
  // { label: '3 回', value: 3 },
  // { label: '6 回', value: 6 },
  // { label: '10 回', value: 10 },
  // { label: '12 回', value: 12 },
  // { label: '18 回', value: 18 },
  // { label: '24 回', value: 24 },
  // { label: '36 回', value: 36 },

  let result = []
  for (let i = 3; i < 61; i++) {
    const obj = {
      label: `${i}回`,
      value: `${i}`
    }
    result.push(obj)
  }
  return result
}

/**
 * ボーナス加算月
 */
export const BonusCombinedMonth = {
  summer: [
    {label: '6月', value: 6},
    {label: '7月', value: 7},
    {label: '8月', value: 8}
  ],

  winter: [
    {label: '12月', value: 12},
    {label: '1月', value: 1}
  ]
}

/**
 * ボーナス一括orボーナス二括の一回目(夏冬固定)
 *
 * ※ 夏冬固定は支払開始月ルール(現行/新)の対象外。申込月ごとの固定テーブルで
 *    最短は常に申込月の翌々月(+2)のままとなる。月指定(FixBonusOnceMonth)のみ
 *    新ルールで翌月起点になる。(2026-08 仕様確認済み・要確認事項として申し送り)
 *
 * @param {string} payment 支払い方法
 * @param {date} appDate 申込日
 */
export const BonusOnceMonth = (payment, appDate) => {
  if (payment !== 'once' && payment !== 'seconds') return []
  const mouthConstants = {
    once: {
      1: [5, 6, 7],
      2: [4, 5, 6],
      3: [3, 4, 5, 9],
      4: [2, 3, 4, 8, 9],
      5: [2, 3, 7, 8],
      6: [2, 6, 7],
      7: [5, 6],
      8: [4, 5],
      9: [3, 4, 9],
      10: [2, 3, 8, 9],
      11: [2, 7, 8, 9],
      12: [6, 7, 8]
    },
    seconds: {
      1: [5, 6, 7],
      2: [4, 5, 6],
      3: [3, 4, 5],
      4: [2, 3, 4, 8, 9],
      5: [2, 3, 7, 8],
      6: [2, 6, 7],
      7: [5, 6],
      8: [4, 5],
      9: [3, 4],
      10: [2, 3, 8, 9, 10],
      11: [2, 7, 8, 9],
      12: [6, 7, 8]
    }
  }
  // const month = {
  //     1: [
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //     ],
  //     2: [
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //     ],
  //     3: [
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //     ],
  //     4: [
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //     ],
  //     5: [
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //     ],
  //     6: [
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //     ],
  //     7: [
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //     ],
  //     8: [
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //     ],
  //     9: [
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //     ],
  //     10: [
  //         {
  //             label: '12月',
  //             value: 12,
  //         },
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //     ],
  //     11: [
  //         {
  //             label: '1月',
  //             value: 1,
  //         },
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //     ],
  //     12: [
  //         {
  //             label: '6月',
  //             value: 6,
  //         },
  //         {
  //             label: '7月',
  //             value: 7,
  //         },
  //         {
  //             label: '8月',
  //             value: 8,
  //         },
  //     ],
  // }
  const applicationMonth = appDate.clone().format('M')

  const choices = mouthConstants[payment][applicationMonth].map((add) => {
    return {
      label: appDate.clone().add(add, 'M').format('YYYY年MM月'),
      value: appDate.clone().add(add, 'M').startOf('month').format('YYYY-MM-DD')
    }
  })
  return choices
}

/**
 * ボーナス一括orボーナス二括の一回目の修正の選択肢
 * @param {string} payment 支払い方法
 * @param {string} category 申込種別 (credit or installment)
 * @param {date} appDate 申込日
 * @param {bool} settingBonusStartPeriod ボ1・ボ2 支払月設定フラグ(毎月10日まで翌月可 or 翌月固定)
 * @param {bool} useNewRule 加盟店の支払開始月ルール(true: 新 = 翌月基準)
 */
export const FixBonusOnceMonth = (payment, category, appDate, settingBonusStartPeriod, useNewRule = false) => {
  if (payment !== 'once' && payment !== 'seconds') return []
  // 設定フラグがtrueかつ1~10日までの申し込み、または新ルールであれば【翌月】起点
  // (ボ1: 申込月の翌月から8ヶ月以内 / ボ2: 申込月の翌月から13ヶ月以内)
  const initialAddMonth = startPeriodAddMonth(appDate, settingBonusStartPeriod, useNewRule)
  const afterNextMonth = appDate.clone().add(initialAddMonth, 'M')
  const addMonths = {
    once: [0, 1, 2, 3, 4, 5, 6, 7],
    seconds: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  }
  const choices = addMonths[payment].map((add) => {
    return {
      label: afterNextMonth.clone().add(add, 'M').format('YYYY年MM月'),
      value: afterNextMonth.clone().add(add, 'M').startOf('month').format('YYYY-MM-DD')
    }
  })
  return choices
}

/**
 * ボーナス二回の第2回目支払月
 * @param {date} once ボーナス一括orボーナス二括の一回目
 * @param {date} appDate 申込日
 */
export const BonusTwiceMonth = (once, appDate) => {
  if (!once || once === '') return []
  const mouthConstants = {
    1: {
      6: [11, 12],
      7: [11, 12],
      8: [11, 12]
    },
    2: {
      6: [10, 11],
      7: [10, 11],
      8: [10, 11]
    },
    3: {
      6: [9, 10],
      7: [9, 10],
      8: [9, 10]
    },
    4: {
      6: [8, 9],
      7: [8, 9],
      8: [8, 9],
      12: [14],
      1: [14]
    },
    5: {
      7: [7, 8],
      8: [7, 8],
      12: [13, 14],
      1: [13, 14]
    },
    6: {
      8: [6, 7],
      12: [12, 13, 14],
      1: [12, 13, 14]
    },
    7: {
      12: [11, 12, 13],
      1: [11, 12, 13]
    },
    8: {
      12: [10, 11, 12],
      1: [10, 11, 12]
    },
    9: {
      12: [9, 10, 11],
      1: [9, 10, 11]
    },
    10: {
      12: [8, 9, 10],
      1: [8, 9, 10],
      6: [14],
      7: [14],
      8: [14]
    },
    11: {
      1: [7, 8, 9],
      6: [13],
      7: [13],
      8: [13]
    },
    12: {
      6: [12],
      7: [12],
      8: [12]
    }
  }
  const applicationMonth = appDate.clone().format('M')
  const onceMonth = moment(once).format('M')
  // const seconds = helperSecondPaymentMonth(applicationMonth,once);
  // return seconds;
  let choices = []
  if (mouthConstants[applicationMonth][onceMonth]) {
    choices = mouthConstants[applicationMonth][onceMonth].map((add) => {
      return {
        label: appDate.clone().add(add, 'M').format('YYYY年MM月'),
        value: appDate.clone().add(add, 'M').startOf('month').format('YYYY-MM-DD')
      }
    })
  }
  return choices
}

/**
 * ボーナス二回の第2回目支払月の修正の選択肢
 * @param {date} once ボーナス一括orボーナス二括の一回目
 * @param {string} category 申込種別 (credit or installment)
 * @param {date} appDate 申込日
 * @param {bool} settingBonusStartPeriod ボ1・ボ2 支払月設定フラグ(毎月10日まで翌月可 or 翌月固定)
 * @param {bool} useNewRule 加盟店の支払開始月ルール(true: 新 = 翌月基準)
 */
export const FixBonusTwiceMonth = (once, category, appDate, settingBonusStartPeriod, useNewRule = false) => {
  if(!once) once = moment().startOf('day').format('YYYY-MM-DD')
  const onceMonth = moment(once)
  // initialAddMonth は 2 のまま動かさないこと。
  // 2回目の起点が「1回目の翌月」になるのは、この 2 と後段の diff 補正
  // (moment の月差が切り捨てられる挙動)が噛み合って成立しているため。
  // 1 にすると2回目の起点が1回目と同じ月になり壊れる。
  let initialAddMonth = 2
  // 1回目の起点が翌月か翌々月かで、2回目に選べる最終月が決まる
  let addMonths = [...Array(twiceMonthRangeLength(startPeriodAddMonth(appDate, settingBonusStartPeriod, useNewRule))).keys()]
  // 支払月1回目と申込日の月の差分に差があれば、支払月以前のデータは表示しないようにする
  const diff = onceMonth.diff(appDate, 'month')
  if(diff) {
    initialAddMonth += diff
    const resultLength = addMonths.length - diff
    addMonths.length = resultLength
  }
  const afterNextMonth = appDate.clone().add(initialAddMonth, 'M')
  const choices = addMonths.map((add) => {
    return {
      label: afterNextMonth.clone().add(add, 'M').format('YYYY年MM月'),
      value: afterNextMonth.clone().add(add, 'M').startOf('month').format('YYYY-MM-DD')
    }
  })
  return choices
}

/**
 * 値の変換key名と条件の配列
 */
export const Conversions = [
  {key: 'cash_total', term: 'money'},
  {key: 'down_payment', term: 'money'},
  {key: 'bonus_combined_addition_amount', term: 'money'},
  {key: 'remaining_money', term: 'money'},
  {key: 'split_total_payment', term: 'money'},
  {key: 'total_pay', term: 'money'},
  {key: 'split_fee', term: 'money'},
  {key: 'payment_amount_once', term: 'money'},
  {key: 'payment_amount_second', term: 'money'},
  {key: 'split_number', term: 'number'}
]

export const allMonth = () => {
  const monthList = []
  for (let m = 1; m <= 12; m++) {
    monthList.push({value: m, label: m + '月'})
  }
  return monthList
}

export const peymentPeriodYears = (appDateY, range = 5) => {
  const years = []
  for (let y = 0; y < range; y++) {
    years.push({value: appDateY + y, label: appDateY + y + '年'})
  }
  return years
}
